import { useParams } from 'react-router-dom';
import {
  useEquipment,
  useEntityLogs,
  useItemProcesses,
  useDepreciationHistory,
  useItems,
} from '@/api/capacities';
import { InventoryDetailView } from '../items/inventory-detail-view';

export interface CapacityEquipmentDetailPageProps {
  /** Equipment id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Equipment detail — identity header (name · service_tag · status) over Overview
 * (model / supplier / location / purchase & warranty / depreciation) and
 * Activity (equipment logs → Timeline) tabs. Read-only. Shares the inventory
 * detail view with components; equipment is keyed on `service_tag`.
 */
export default function CapacityEquipmentDetailPage({ id: idProp }: CapacityEquipmentDetailPageProps) {
  const params = useParams();
  const id = idProp ?? params.id ?? '';

  const { data: equipment, isLoading, isError, refetch } = useEquipment(id);
  const {
    data: logsData,
    isLoading: logsLoading,
    isError: logsError,
    refetch: refetchLogs,
  } = useEntityLogs('equipment', id);
  const {
    data: processesData,
    isLoading: processesLoading,
    isError: processesError,
    refetch: refetchProcesses,
  } = useItemProcesses(id);
  const {
    data: depreciationData,
    isLoading: depreciationLoading,
    isError: depreciationError,
  } = useDepreciationHistory(id);
  // Child components installed in this equipment (legacy parent_id filter).
  const { data: componentsData } = useItems({ parent_id: id, item_type: 'component', page_size: 200 });

  return (
    <InventoryDetailView
      backTo="/capacities/items"
      backLabel="Inventory"
      noun="Equipment"
      reference={equipment?.service_tag ? `#${equipment.service_tag}` : undefined}
      item={equipment}
      linkedComponents={componentsData?.results ?? []}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      logs={logsData?.results ?? []}
      logsLoading={logsLoading}
      logsError={logsError}
      onRetryLogs={() => refetchLogs()}
      processes={processesData?.results ?? []}
      processesLoading={processesLoading}
      processesError={processesError}
      onRetryProcesses={() => refetchProcesses()}
      depreciation={depreciationData?.results ?? []}
      depreciationLoading={depreciationLoading}
      depreciationError={depreciationError}
    />
  );
}
